/**
 * Vincent P1.8.b (Eric 2026-07-31 SPEC STRICK PROD) — Extraction du hook
 * `useVideoDeepLink` depuis `VideoDetailPage.js`.
 *
 * Absorbe toute la logique de deep-linking URL de la fiche vidéo :
 *   - Parse `?t=`, `?tab=`, `?source=`, `?focus=` une seule fois au mount.
 *   - Calcule le panel initial (whitelist) selon `?tab=`.
 *   - Effet B613 : quand `?focus=<ku_id>` + `?tab=qa|scripts|procedures`,
 *     force le sous-onglet à « edit » pour les éditeurs (là où la liste
 *     avec data-focus-target est rendue).
 *   - Effet B621 : quand `?focus=<ku_id>` cible une Q&R / Script /
 *     Procédure / Objection, ouvre auto le KnowledgeDetailModal (Concepts
 *     sont déjà gérés par KnowledgeTab.onEditItem).
 *
 * Reste dans VideoDetailPage :
 *   - Les states `qaSubtab`, `scriptsSubtab`, `proceduresSubtab`,
 *     `activePanel` (mutualisés avec d'autres useEffects/handlers).
 *   - L'appel à `useFocusFromURL` (scroll + highlight de l'item ciblé).
 */
import { useEffect, useMemo, useRef } from "react";

const VALID_TABS = ["transcript", "speakers", "qa", "scripts", "procedures", "objections", "knowledge", "synthesis", "favorites"];

export function useVideoDeepLink({
  canEditKnowledge,
  setQaSubtab,
  setScriptsSubtab,
  setProceduresSubtab,
  knowledgeUnits,
  setDetailModalKu,
}) {
  // Snapshot des params URL au premier rendu (on ne relit pas pour éviter les
  // sauts d'onglet si l'utilisateur clique sur un autre lien manuellement).
  const params = useMemo(() => {
    if (typeof window === "undefined") return {};
    const sp = new URLSearchParams(window.location.search);
    return {
      startTime: parseInt(sp.get("t") || "0", 10) || 0,
      urlTab: sp.get("tab"),
      urlSource: sp.get("source"),
      focusId: sp.get("focus"),
    };
  }, []);

  // Vincent B621 (Eric 2026-07-30 SPEC STRICK) — Whitelist des tabs valides.
  // Auparavant `?tab=objections` retombait sur "transcript" → useFocusFromURL
  // échouait silencieusement sur les favoris Objection.
  // Vincent B827 v2 (Eric 2026-02-14 SPEC STRICK PROD) — Pour les non-éditeurs
  // (apprenants), l'onglet par défaut devient « Synthèse » (le contenu
  // pédagogique le plus utile pour eux). Les éditeurs gardent « Transcript »
  // par défaut (débug pipeline). L'URL `?tab=` reste prioritaire sur ce
  // défaut afin que les deep-links continuent de marcher.
  const defaultTab = canEditKnowledge ? "transcript" : "synthesis";
  const initialActivePanel = VALID_TABS.includes(params.urlTab) ? params.urlTab : defaultTab;

  // Vincent B613 — Auto-switch sous-onglet vers « edit » quand focus est présent.
  useEffect(() => {
    if (!params.focusId) return;
    if (!canEditKnowledge) return; // les non-éditeurs sont déjà en "view"
    if (params.urlTab === "qa") setQaSubtab("edit");
    else if (params.urlTab === "scripts") setScriptsSubtab("edit");
    else if (params.urlTab === "procedures") setProceduresSubtab("edit");
  }, [canEditKnowledge, params.focusId, params.urlTab]);

  // Vincent B621 — Auto-ouverture du KnowledgeDetailModal sur ?focus=<ku_id>.
  const autoOpenedFocusRef = useRef(null);
  useEffect(() => {
    if (!knowledgeUnits?.length) return;
    if (!params.focusId) return;
    if (autoOpenedFocusRef.current === params.focusId) return;
    const target = knowledgeUnits.find((u) => u.id === params.focusId);
    if (!target) return;
    // Concepts déjà gérés par KnowledgeTab.onEditItem — skip pour éviter
    // de doubler l'ouverture.
    if (target.type === "concept") return;
    autoOpenedFocusRef.current = params.focusId;
    setDetailModalKu(target);
  }, [knowledgeUnits, params.focusId, setDetailModalKu]);

  return {
    startTime: params.startTime,
    urlTab: params.urlTab,
    urlSource: params.urlSource,
    focusId: params.focusId,
    initialActivePanel,
  };
}
